import { lazy, PropsWithChildren, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { show } from '@intercom/messenger-js-sdk';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, ButtonBase, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

import Analytics from '~/assets/icons/sidebar/analytics.svg?react';
import ArrowsToggle from '~/assets/icons/sidebar/arrows-toggle.svg?react';
import BurgerMenu from '~/assets/icons/sidebar/burger-menu.svg?react';
import ContactUs from '~/assets/icons/sidebar/contact-us.svg?react';
import CreateQrIcon from '~/assets/icons/sidebar/create-qr.svg?react';
import MoneyIcon from '~/assets/icons/sidebar/money.svg?react';
import MyAccountIcon from '~/assets/icons/sidebar/my-account.svg?react';
import MyQrIcon from '~/assets/icons/sidebar/my-qr.svg?react';
import QRFullLogo from '~/assets/icons/sidebar-logo-new.svg?react';
import MobileLogo from '~/assets/icons/svgs/mobile-logo.svg?react';
import { Button } from '~/components/ui/button';
import Loader from '~/components/ui/Loader';
import { Text } from '~/components/ui/text';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { QR_CODE_ID_PARAM_NAME } from '~/constants/qr-code';
import { PATH } from '~/constants/route-path';
import { useAppDispatch } from '~/hooks/useRedux';
import { SubscriptionPopup } from '~/pages/MyQR/components/SubscriptionPopup';
import { getLoggedInUser } from '~/store/reducers/account/selector';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import { stepperSlice } from '~/store/reducers/stepepr';
import useSidebarStore from '~/store/useSidebarStore';
import { COLORS } from '~/styles/colors';

import { Drawer, DrawerHeader } from '../../styled';
import * as S from '../../styled';

const MobileHeaderStepper = lazy(() => import('../MobileHeaderStepper'));

const sidebarLinks = [
  { name: 'newQr', path: PATH.QR.CREATE, Icon: () => <CreateQrIcon /> },
  { name: 'analytics', path: PATH.ANALYTICS, isVisibleWithoutSubscription: false, Icon: () => <Analytics /> },
  { name: 'myQRCodes', path: PATH.QR.MAIN, isVisibleWithoutSubscription: true, Icon: () => <MyQrIcon /> },
  { name: 'myAccount', path: PATH.SETTINGS, isVisibleWithoutSubscription: true, Icon: () => <MyAccountIcon /> },
  { name: 'payments', path: PATH.PAYMENTS.MAIN, isVisibleWithoutSubscription: true, Icon: () => <MoneyIcon /> },
];

type SidebarProps = PropsWithChildren<{}>;
export function Sidebar({ children }: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(DESKTOP_MIN_WIDTH));
  const [searchParams] = useSearchParams();

  const qrCode = useSelector(getQrCodeReducerData);
  const { isSubscriptionValid, subscriptionInfo, loading } = useSelector(getLoggedInUser);

  const qrCodeId = searchParams.get(QR_CODE_ID_PARAM_NAME);
  const isQrcodeDataExist = !!qrCode.data.id || !!qrCodeId;

  const { isDesktopSidebarOpen, setIsDesktopSidebarOpen } = useSidebarStore();

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [subscriptionPopupOpen, setSubscriptionPopupOpen] = useState(false);

  const isActiveItem = (tab) => {
    if (tab.path === PATH.QR.CREATE) {
      return location.pathname === PATH.QR.CREATE || location.pathname.includes(PATH.QR.CREATE_STATIC);
    }

    return location.pathname === tab.path;
  };

  const currentPeriodEnd = subscriptionInfo?.currentPeriodEndRaw && new Date(subscriptionInfo.currentPeriodEndRaw);
  const difference = currentPeriodEnd?.getTime() - Date.now();
  const daysRemaining = Math.floor(difference / (1000 * 60 * 60 * 24));
  const navigateHandler = (item) => {
    if (
      (isQrcodeDataExist && location.pathname === PATH.QR.CREATE) ||
      location.pathname.includes(PATH.QR.CREATE_STATIC) ||
      location.pathname.includes('/app/edit_qr_static')
    ) {
      dispatch(stepperSlice?.actions?.setRedirectRoute(item?.path));

      return location.pathname + location.search;
    }

    if (isSubscriptionValid) {
      return item?.path;
    }

    if (!isSubscriptionValid && item?.isVisibleWithoutSubscription) {
      return item?.path;
    }

    return PATH.PAYMENTS.MAIN;
  };

  const handleLogoClick = () => {
    if (isQrcodeDataExist && location.pathname === PATH.QR.CREATE) {
      dispatch(stepperSlice?.actions?.setRedirectRoute(PATH.QR.MAIN));

      return;
    }

    navigate(PATH.QR.MAIN);
  };

  const hasTrial = daysRemaining >= 0 && !subscriptionInfo?.interval;

  const shouldRenderUpgradeBox =
    !!isDesktopSidebarOpen &&
    subscriptionInfo?.status &&
    (!subscriptionInfo?.interval || subscriptionInfo?.status === 'inactive');

  const displayMobileStepper =
    location.pathname.includes('create_qr') ||
    location.pathname.includes('create_qr_static') ||
    location.pathname.includes('edit_qr_static');

  return (
    <Box sx={S.SidebarWrapper}>
      {isMobile && (
        <>
          <Box sx={S.MobileHeader}>
            <ButtonBase onClick={handleLogoClick}>
              {displayMobileStepper ? <MobileLogo /> : <QRFullLogo width={128} height={30} />}
            </ButtonBase>
            <Suspense fallback={<Loader />}>{displayMobileStepper && <MobileHeaderStepper />}</Suspense>
            <ButtonBase onClick={() => setIsMobileOpen((prev) => !prev)}>
              <MenuIcon />
            </ButtonBase>
          </Box>
          {isMobileOpen && <Box sx={S.MobileOverlay} onClick={() => setIsMobileOpen((prev) => !prev)}></Box>}
        </>
      )}

      <Drawer
        variant="permanent"
        open={isDesktopSidebarOpen}
        ModalProps={{
          onBackdropClick: () => setIsDesktopSidebarOpen(false),
        }}
        sx={[
          !isDesktopSidebarOpen && {
            width: '160px !important',
          },
          S.Sidebar,
        ]}
        PaperProps={{
          sx: {
            width: isDesktopSidebarOpen ? 'unset' : '120px !important',
            ...S.PaperStyles,
          },
          className: isMobileOpen ? 'mobile-sidebarOpen' : '',
        }}
      >
        <DrawerHeader sx={S.SidebarHeader}>
          {isDesktopSidebarOpen && (
            <ButtonBase onClick={handleLogoClick}>
              <QRFullLogo />
            </ButtonBase>
          )}
          {isMobile ? (
            <ButtonBase onClick={() => setIsMobileOpen((prev) => !prev)}>
              <CloseIcon />
            </ButtonBase>
          ) : (
            <Box onClick={() => setIsDesktopSidebarOpen(!isDesktopSidebarOpen)} sx={{ padding: '4px' }}>
              {isDesktopSidebarOpen ? <ArrowsToggle /> : <BurgerMenu />}
            </Box>
          )}
        </DrawerHeader>
        <List sx={S.SidebarMenuList} className={isDesktopSidebarOpen ? '' : 'closed-list'}>
          {sidebarLinks.map((route, index) => {
            const { Icon } = route;
            const isActive = isActiveItem(route);

            return (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  onClick={() => {
                    navigate(navigateHandler(route));
                    if (isMobile) {
                      setIsMobileOpen(false);
                    }
                  }}
                  sx={S.MenuBtn}
                  className={isActive ? 'btn-menuItem active-menuItem' : 'btn-menuItem'}
                >
                  <Icon />
                  {isDesktopSidebarOpen && (
                    <ListItemText
                      primary={
                        <Text className="menuItem-text" variant="h3">
                          {t(`sidebar:${route.name}`)}
                        </Text>
                      }
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
          <ListItem disablePadding>
            <ListItemButton onClick={show} sx={S.MenuBtn} className="btn-menuItem contact-us" disableRipple>
              <ContactUs />
              {isDesktopSidebarOpen && (
                <ListItemText
                  primary={
                    <Text className="menuItem-text" variant="h3">
                      {t('contactUs:title')}
                    </Text>
                  }
                />
              )}
            </ListItemButton>
          </ListItem>
          {/*<ListItem className="logout-side">*/}
          {/*  <Button type="outlined" onClick={handleLogOutClick}>*/}
          {/*    {isDesktopSidebarOpen ? t('buttons:logOutBtn') : <LogoutIcon />}*/}
          {/*  </Button>*/}
          {/*</ListItem>*/}
        </List>
        {!loading ? (
          <>
            {shouldRenderUpgradeBox && (
              <Box
                sx={{
                  background: hasTrial ? '#E6F3FD' : 'linear-gradient(90deg, #D32F2F 0%, #AE1F1F 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '18px',
                  padding: '24px',
                  borderRadius: '8px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {!hasTrial && (
                    <Text variant="t16Regular" sx={{ color: hasTrial ? COLORS.primary : COLORS.white }}>
                      {t('alerts:subscriptionExpired')}
                    </Text>
                  )}
                </Box>
                <Button
                  onClick={() => setSubscriptionPopupOpen(true)}
                  variant="contained"
                  sx={{ backgroundColor: hasTrial ? 'unset' : 'white !important' }}
                  size="small"
                >
                  <Text variant="buttonMedium" sx={{ color: hasTrial ? 'unset' : COLORS.darkRed }}>
                    {t('buttons:upgradeNow')}
                  </Text>
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '30px' }}>
            <CircularProgress size={30} />
          </Box>
        )}

        {subscriptionPopupOpen && (
          <SubscriptionPopup
            onClose={setSubscriptionPopupOpen}
            open={subscriptionPopupOpen}
            endTime={subscriptionInfo?.currentPeriodEndRaw}
          />
        )}
      </Drawer>
      {children}
    </Box>
  );
}
